import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import SearchOverlay from "./SearchOverlay";
import { ShoppingBag, Search, Menu, X, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/packages", label: "Packages" },
  { to: "/brands", label: "Brands" },
  { to: "/installation", label: "Installation" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const { count, wishlist } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("scroll", onScroll);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("keydown", onKey); };
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass-strong" : "bg-white/60 backdrop-blur-md"}`} data-testid="site-nav">
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3.5 group" data-testid="brand-logo">
          <div className="relative w-11 h-11 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border" style={{ borderColor: "rgba(15,12,8,0.14)" }}></div>
            <div className="absolute inset-1.5 rounded-full border-2" style={{ borderColor: "#8A6A0F" }}></div>
            <div className="w-1.5 h-1.5 rounded-full group-hover:scale-125 transition duration-500" style={{ background: "#8A6A0F" }}></div>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-[22px] tracking-[0.06em] font-medium" style={{ color: "#0D0C0A", letterSpacing: "0.08em" }}>
              HomeTheatres <em className="not-italic" style={{ color: "#8A6A0F" }}>India</em>
            </span>
            <span className="text-[9px] mt-1.5" style={{ fontFamily: "'JetBrains Mono', monospace", color: "rgba(15,12,8,0.5)", letterSpacing: "0.28em" }}>EST · 1985 · DEHRADUN</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => `text-sm font-semibold relative py-2 transition ${isActive ? "brass-text" : "text-black/80 hover:text-black"}`}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <span>
                  {n.label}
                  <span className={`absolute -bottom-0.5 left-0 h-px transition-all ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} style={{ background: "#A67C00" }}></span>
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => setSearchOpen(true)} aria-label="Search" className="p-2.5 rounded-full hover:bg-black/5 transition" data-testid="search-toggle">
            <Search className="w-4 h-4" />
          </button>
          <Link to="/cart" className="relative p-2.5 rounded-full hover:bg-black/5 transition" data-testid="nav-wishlist">
            <Heart className="w-4 h-4" />
            {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" style={{ background: "#A67C00" }}>{wishlist.length}</span>}
          </Link>
          <Link to="/cart" className="relative p-2.5 rounded-full hover:bg-black/5 transition" data-testid="nav-cart">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" style={{ background: "#A67C00" }}>{count}</span>}
          </Link>
          <Link to="/quote" className="btn-gold hidden md:inline-flex text-xs px-5 py-2.5 ml-2" data-testid="cta-get-quote">Get a Quote</Link>
          <button className="lg:hidden p-2.5" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => { setSearchOpen(false); setOpen(false); }} />

      {open && (
        <div className="lg:hidden glass-strong border-t border-black/8" data-testid="mobile-nav">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={({ isActive }) => `text-lg ${isActive ? "brass-text" : "text-black/80"}`}>{n.label}</NavLink>
            ))}
            <Link to="/quote" className="btn-gold justify-center mt-2" onClick={() => setOpen(false)} data-testid="mobile-cta-get-quote">Get a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}
